import { useTranslation } from "@refinedev/core";

import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";

import { VerificationCodeInput } from "@/components/auth";
import { CardButton } from "@/components/common";
import { PhoneInput } from "@/components/common";

export interface MfaSmsSetupProps {
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
  verificationCode: string;
  onCodeChange: (value: string) => void;
  onSendCode: () => void;
  onVerify: () => void;
  isSubmitting: boolean;
  codeSent: boolean;
  errorMessage: string;
  showSetPreferred?: boolean;
  setAsPreferred?: boolean;
  onSetPreferredChange?: (value: boolean) => void;
  submitButtonLabel?: string;
  /** Whether to show the email recovery info message */
  showEmailRecoveryInfo?: boolean;
}

/**
 * Reusable SMS MFA setup component.
 * Used in both setup-mfa.tsx and profile ChangeMFAPanel.
 */
export const MfaSmsSetup: React.FC<MfaSmsSetupProps> = ({
  phoneNumber,
  onPhoneNumberChange,
  verificationCode,
  onCodeChange,
  onSendCode,
  onVerify,
  isSubmitting,
  codeSent,
  errorMessage,
  showSetPreferred = false,
  setAsPreferred = false,
  onSetPreferredChange,
  submitButtonLabel,
  showEmailRecoveryInfo = false,
}) => {
  const { translate } = useTranslation();

  return (
    <Stack spacing={2} mt={1.25}>
      {/* Instructions */}
      <Typography variant="body2" color="text.secondary">
        {translate("pages.mfa.smsInstructions", "An authentication code will be sent to the phone number you provide")}
      </Typography>

      {/* Phone Number Label */}
      <Typography variant="body2" color="primary">
        {translate("pages.mfa.phoneLabel", "Phone Number")}{" "}
        <Typography component="span" color="secondary">
          *
        </Typography>
      </Typography>

      {/* Phone Input + Send Button */}
      <Stack direction="row" spacing={3} alignItems={"center"}>
        <PhoneInput value={phoneNumber} onChange={onPhoneNumberChange} disabled={isSubmitting} />
        <CardButton
          onClick={onSendCode}
          disabled={!phoneNumber.trim() || isSubmitting}
          fullWidth={false}
          sx={{ px: 4 }}
        >
          {translate(isSubmitting ? "pages.mfa.sending" : "pages.mfa.sendCode")}
        </CardButton>
      </Stack>

      {/* Set as Preferred Question (Profile only) */}
      {showSetPreferred && codeSent && (
        <Typography variant="caption" color="primary">
          {translate("pages.profile.mfa.askSetPreferred", { method: "SMS" })}
        </Typography>
      )}

      {/* Email recovery info - shown below preferred question */}
      {showEmailRecoveryInfo && codeSent && (
        <Typography variant="caption" color="primary">
          {translate("pages.profile.mfa.emailRecoveryInfo")}
        </Typography>
      )}

      {/* Set as Preferred Checkbox (Profile only) */}
      {showSetPreferred && codeSent && (
        <FormControlLabel
          control={
            <Checkbox
              checked={setAsPreferred}
              onChange={e => onSetPreferredChange?.(e.target.checked)}
              size="small"
              disabled={isSubmitting}
            />
          }
          label={<Typography variant="body1">{translate("pages.mfa.setSmsPreferred")}</Typography>}
        />
      )}

      {/* Code Entry Instructions */}
      <Typography variant="body2" color="text.hint">
        {translate("pages.mfa.enterCodeInstructions", "After receiving a six-digit code, enter the code below")}
      </Typography>

      {/* 6-Digit Code Input */}
      <VerificationCodeInput value={verificationCode} onChange={onCodeChange} disabled={isSubmitting} />

      {/* Error Message */}
      {errorMessage && (
        <Typography variant="body2" color="error.dark" align="center">
          {errorMessage}
        </Typography>
      )}

      {/* Verify Button */}
      <CardButton onClick={onVerify} disabled={verificationCode.length !== 6 || isSubmitting}>
        {isSubmitting
          ? translate(submitButtonLabel ? "pages.profile.mfa.enablingButton" : "pages.mfa.verifying")
          : (submitButtonLabel ?? translate("pages.mfa.verify"))}
      </CardButton>
    </Stack>
  );
};
